/**
 * The host's confirm sheet (ADR-029 §4) — drawn when an app asks to act
 * outside Fias in the person's name (post as their account). The
 * `action-confirmation` client-side entity opens it with a pending action's id.
 *
 * Every word is the platform's: the preview was rendered by the server from
 * the validated call when it was recorded, and names the account the action
 * speaks as and — in an org workspace — the workspace that account belongs
 * to. The plugin supplies only the id. The answer is written by the host
 * session (`POST /v1/plugin-actions/:id/approve|decline`); the app runs the
 * action only by claiming that approval, once.
 *
 * Nothing the app wrote may be hidden from the person approving it:
 *   - every image the action would post is SHOWN, as the server verified it
 *     against the bytes the approval pins (an image that changed, or cannot be
 *     shown, blocks approval — the person cannot approve what they cannot see);
 *   - invisible and direction-changing characters are drawn as visible marks,
 *     and long runs of blank lines collapse to a labelled marker;
 *   - Approve stays off until the text has been scrolled to its end.
 */

import { useCallback, useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Loader2, Send } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { api } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import { useConsentArmDelay } from '@/lib/hooks/useConsentArmDelay';
import type { ActionConfirmationResult } from '@fias/client-entities';

/**
 * Characters that change what a reader sees without being seen: bidi
 * overrides and isolates, zero-width characters, and every other control
 * character except newline and tab.
 */
const HIDDEN_CHARACTERS =
  // eslint-disable-next-line no-control-regex -- matching control characters is the point
  /[\u0000-\u0008\u000b-\u001f\u007f-\u009f\u061c\u200b-\u200f\u202a-\u202e\u2060-\u2064\u2066-\u2069\ufeff]/g;

/** More blank lines than this in a row collapse to one labelled marker. */
const MAX_BLANK_RUN = 2;

function Marker({ children }: { children: ReactNode }) {
  return (
    <span className="mx-0.5 rounded bg-amber-100 px-1 font-mono text-xs text-amber-900 dark:bg-amber-900/40 dark:text-amber-100">
      {children}
    </span>
  );
}

/** One detail line as the person must see it: nothing invisible, nothing pushed out of view. */
export function VisibleText({ text }: { text: string }) {
  const out: ReactNode[] = [];
  let blank = 0;
  const flushBlank = (key: string) => {
    if (blank > MAX_BLANK_RUN) {
      out.push(<Marker key={key}>{`${blank} blank lines`}</Marker>, '\n');
    } else {
      for (let i = 0; i < blank; i++) out.push('\n');
    }
    blank = 0;
  };
  text.split('\n').forEach((line, i, lines) => {
    if (line.trim() === '' && i < lines.length - 1) {
      blank += 1;
      return;
    }
    flushBlank(`b${i}`);
    let last = 0;
    for (const match of line.matchAll(HIDDEN_CHARACTERS)) {
      const at = match.index ?? 0;
      if (at > last) out.push(line.slice(last, at));
      const code = match[0].codePointAt(0)!.toString(16).toUpperCase().padStart(4, '0');
      out.push(<Marker key={`c${i}-${at}`}>{`U+${code}`}</Marker>);
      last = at + match[0].length;
    }
    if (last < line.length) out.push(line.slice(last));
    if (i < lines.length - 1) out.push('\n');
  });
  return <>{out}</>;
}

type MediaState = { url: string } | { problem: 'changed' | 'unavailable' } | undefined;

/** The images an action would post, fetched one by one as the server verified them. */
function useActionMedia(pendingActionId: string, count: number): MediaState[] {
  const [media, setMedia] = useState<MediaState[]>([]);
  useEffect(() => {
    let live = true;
    const urls: string[] = [];
    setMedia(Array.from({ length: count }, () => undefined));
    for (let i = 0; i < count; i++) {
      void api.getPluginActionMedia(pendingActionId, i).then(
        (result) => {
          if (!live) return;
          let next: MediaState;
          if ('blob' in result) {
            const url = URL.createObjectURL(result.blob);
            urls.push(url);
            next = { url };
          } else {
            next = { problem: result.problem };
          }
          setMedia((prev) => prev.map((m, j) => (j === i ? next : m)));
        },
        () => {
          if (live) {
            setMedia((prev) => prev.map((m, j) => (j === i ? { problem: 'unavailable' } : m)));
          }
        },
      );
    }
    return () => {
      live = false;
      urls.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [pendingActionId, count]);
  return media;
}

/** Credits as the person reads them: whole, or to two places when small. */
function formatCredits(credits: number): string {
  return credits >= 10 ? String(Math.round(credits)) : credits.toFixed(2).replace(/\.?0+$/, '');
}

/**
 * A scheduled call's time in the viewer's own zone ("Thu, Oct 9, 2:00 AM PDT"),
 * shown beside the server's UTC wording so the time approved is the time meant.
 */
function formatLocalTime(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short',
  });
}

export interface ActionConfirmationSheetProps {
  open: boolean;
  /** The arche that asked. An action written for any other arche is refused, not shown. */
  archeId: string;
  pendingActionId: string;
  onResolve: (result: ActionConfirmationResult) => void;
}

export function ActionConfirmationSheet({
  open,
  archeId,
  pendingActionId,
  onResolve,
}: ActionConfirmationSheetProps) {
  const action = useQuery({
    queryKey: queryKeys.pluginActions.detail(pendingActionId),
    queryFn: () => api.getPluginAction(pendingActionId).then((r) => r.action),
    staleTime: Infinity,
    retry: false,
  });
  const [answering, setAnswering] = useState<'approve' | 'decline' | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const armed = useConsentArmDelay(pendingActionId);
  const settled = useRef(false);

  // An app can only ask about its own actions: another app's id would draw a
  // sheet for something this app never asked to do.
  const foreign = action.data !== undefined && action.data.archeId !== archeId;
  const data = foreign ? undefined : action.data;
  const pending = data?.state === 'pending';
  const preview = data?.preview;

  const media = useActionMedia(pendingActionId, pending ? (preview?.media?.length ?? 0) : 0);
  const mediaChanged = media.some(
    (m) => m !== undefined && 'problem' in m && m.problem === 'changed',
  );
  const mediaReady = media.every((m) => m !== undefined && 'url' in m);

  // Approve waits until the text has been seen to its end: content pushed
  // below the fold (a scam link after a wall of blank lines) is still content.
  const detailsRef = useRef<HTMLDivElement>(null);
  const [readToEnd, setReadToEnd] = useState(false);
  const checkReadToEnd = useCallback(() => {
    const el = detailsRef.current;
    if (el && el.scrollTop + el.clientHeight >= el.scrollHeight - 4) setReadToEnd(true);
  }, []);
  useLayoutEffect(() => {
    setReadToEnd(false);
    checkReadToEnd();
  }, [preview, checkReadToEnd]);

  const resolve = (result: ActionConfirmationResult) => {
    if (settled.current) return;
    settled.current = true;
    onResolve(result);
  };

  const answer = async (decision: 'approve' | 'decline') => {
    setProblem(null);
    setAnswering(decision);
    try {
      const { action: decided } = await api.decidePluginAction(pendingActionId, decision);
      resolve({ approved: decided.state === 'approved' });
    } catch {
      setProblem(
        decision === 'approve'
          ? 'Could not approve this. It may have expired; ask the app to try again.'
          : 'Could not record your answer. Close this to decline.',
      );
    } finally {
      setAnswering(null);
    }
  };

  // Closing the sheet any other way is a decline — recorded, so the pending
  // action can never be approved later from somewhere else.
  const dismiss = () => {
    if (pending) {
      void api.decidePluginAction(pendingActionId, 'decline').catch(() => undefined);
    }
    resolve({ approved: false });
  };

  return (
    <Dialog open={open} onOpenChange={(next) => !next && dismiss()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Send className="h-5 w-5" aria-hidden />
            {preview ? `${preview.appName} wants to: ${preview.summary}` : 'Approve an action'}
          </DialogTitle>
          <DialogDescription>
            {preview?.account
              ? `It will act as ${preview.account}${
                  preview.workspace ? `, the account of ${preview.workspace}` : ''
                }. Nothing is sent unless you approve.`
              : 'Nothing is sent unless you approve.'}
          </DialogDescription>
        </DialogHeader>

        {action.isLoading && (
          <div className="flex justify-center py-6" role="status">
            <Loader2 className="h-5 w-5 animate-spin" aria-label="Loading" />
          </div>
        )}

        {(action.isError || foreign) && (
          <p className="text-sm text-destructive" role="alert">
            This request could not be found. Ask the app to try again.
          </p>
        )}

        {preview?.at && (
          <p className="text-sm font-medium" data-testid="scheduled-local-time">
            Sends {formatLocalTime(preview.at)} your time
          </p>
        )}

        {preview && (
          <div
            ref={detailsRef}
            onScroll={checkReadToEnd}
            tabIndex={0}
            role="region"
            aria-label="What will be sent"
            className="max-h-72 space-y-2 overflow-y-auto rounded-md border border-border bg-muted/40 p-3 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            {preview.details.map((line, i) => (
              <p key={i} className="whitespace-pre-wrap break-words text-sm">
                <VisibleText text={line} />
              </p>
            ))}
            {media.length > 0 && (
              <div className="grid grid-cols-2 gap-2" data-testid="action-media">
                {media.map((m, i) =>
                  m !== undefined && 'url' in m ? (
                    <img
                      key={i}
                      src={m.url}
                      alt={`Image ${i + 1} that will be posted`}
                      className="max-h-40 w-full rounded border border-border object-contain"
                    />
                  ) : (
                    <div
                      key={i}
                      className="flex h-24 items-center justify-center rounded border border-dashed border-border text-xs text-muted-foreground"
                    >
                      {m === undefined ? (
                        <Loader2 className="h-4 w-4 animate-spin" aria-label="Loading image" />
                      ) : (
                        'Image could not be shown'
                      )}
                    </div>
                  ),
                )}
              </div>
            )}
          </div>
        )}

        {pending && preview?.estimatedCredits !== undefined && (
          <p className="text-sm text-muted-foreground" data-testid="action-cost">
            {`Costs about ${formatCredits(preview.estimatedCredits)} credits, charged to you${
              preview.at ? ' when it is sent' : ''
            }.`}
          </p>
        )}

        {pending && mediaChanged && (
          <p className="text-sm text-destructive" role="alert">
            An image changed after the app asked. Decline, and ask the app to try again.
          </p>
        )}

        {pending && preview && !readToEnd && (
          <p className="text-xs text-muted-foreground">Scroll to the end to approve.</p>
        )}

        {data && !pending && (
          <p className="text-sm text-muted-foreground" role="status">
            {data.state === 'expired'
              ? 'This request expired. Ask the app to try again.'
              : 'This request was already answered.'}
          </p>
        )}

        {problem && (
          <p className="text-sm text-destructive" role="alert">
            {problem}
          </p>
        )}

        <DialogFooter className="gap-2">
          <Button
            variant="ghost"
            disabled={answering !== null}
            onClick={() => (pending ? void answer('decline') : resolve({ approved: false }))}
          >
            {pending ? 'Decline' : 'Close'}
          </Button>
          {pending && (
            <Button
              disabled={!armed || answering !== null || !readToEnd || !mediaReady}
              onClick={() => void answer('approve')}
            >
              {answering === 'approve' && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden />
              )}
              Approve
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
